import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Search,
  Filter,
  ArrowRight,
  Star,
  Recycle,
  Award,
  Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Product image mapping
  const productImages = {
    "All-Weather-5400": "/images/allweather5400.webp",
    "All-Weather GM Custom Fit Logo": "/images/allweather-gm-custom-fit-logo.webp", 
    "All-Weather-5600 Logo": "/images/allweather5600-logo.webp",
    "All-Weather 5400 Logo": "/images/allweather5400-logo.webp",
    "Universal All-Weather Logo": "/images/holland-auto-group.webp",
    "Carpet 6000": "/images/carpet6000.webp",
    "Carpet-Euro 6000": "/images/carpet-euro6000.webp",
    "Custom Cuts": "/images/customcuts-1.webp",
    "Felt": "/images/felt-2.webp",
    "Utility Mat Logo": "/images/utility-mat-logo.webp",
    "Freightliner All-Weather Logo": "/images/freightliner-allweather-thumb.webp",
    "Kenworth Universal All-Weather Logo (New T680)": "/images/kenworth-universal-allweather-thumb.webp",
    "Kenworth Universal All-Weather Logo (T600, T660, T800, W900)": "/images/kenworth-universal-allweather-thumb-2.webp"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories and products in parallel
        const [categoriesResponse, productsResponse] = await Promise.all([
          fetch(`${API}/products/categories`),
          fetch(`${API}/products`)
        ]);

        const categoriesResult = await categoriesResponse.json();
        const productsResult = await productsResponse.json();

        if (categoriesResult.success) {
          setCategories(['All', ...categoriesResult.data.map(cat => cat.name)]);
        }

        if (productsResult.success) {
          setProducts(productsResult.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to empty arrays if API fails
        setCategories(['All']);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'category':
        return a.category.localeCompare(b.category);
      case 'price':
        const aPrice = typeof a.pricing === 'object' ? a.pricing.fourPiece || a.pricing.single : 0;
        const bPrice = typeof b.pricing === 'object' ? b.pricing.fourPiece || b.pricing.single : 0;
        return aPrice - bPrice;
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-r from-automats-steel to-gray-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-6">
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200/20 rounded w-64 mx-auto mb-4"></div>
                <div className="h-6 bg-gray-200/20 rounded w-96 mx-auto"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced Header */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <Badge className="bg-automats-red/10 text-automats-red border-automats-red/20 px-6 py-2 text-sm font-body">
              Product Explorer
            </Badge>
            <div className="space-y-4">
              <h1 className="text-5xl lg:text-6xl font-heading font-bold text-automats-black leading-tight">
                Premium Floor Mats for Every Need
              </h1>
              <p className="text-xl font-body text-automats-steel max-w-4xl mx-auto leading-relaxed">
                Discover our complete range of 100% recycled automotive floor mats. 
                From all-weather protection to custom solutions, find the perfect fit for your customers.
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="flex items-center space-x-3 bg-automats-off-white px-6 py-4 rounded-xl">
                <div className="bg-automats-eco-green p-2 rounded-full">
                  <Recycle className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <span className="font-body font-semibold text-automats-black block">100% Recycled Materials</span>
                  <span className="font-body text-xs text-automats-steel">Superior quality, not compromised</span>
                </div>
              </div>
              <div className="flex items-center space-x-3 bg-automats-off-white px-6 py-4 rounded-xl">
                <div className="bg-automats-red p-2 rounded-full">
                  <Award className="h-5 w-5 text-white" />
                </div>
                <span className="font-body font-semibold text-automats-black">American Made Since 1985</span>
              </div>
              <div className="flex items-center space-x-3 bg-automats-off-white px-6 py-4 rounded-xl">
                <div className="bg-automats-steel p-2 rounded-full">
                  <Star className="h-5 w-5 text-white" />
                </div>
                <span className="font-body font-semibold text-automats-black">Industry Leading Quality</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <a
                href="mailto:sales@automats.us?subject=Product Quote Request&body=Hello, I would like to request a quote for AutoMats products."
                className="bg-automats-red hover:bg-red-700 text-white px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Request Quote
              </a>
              <a 
                href="/images/AutoMats_Brochure.pdf"
                download="AutoMats_Product_Catalog.pdf"
                className="border-2 border-automats-steel text-automats-steel hover:bg-automats-steel hover:text-white px-8 py-4 rounded-xl font-body font-semibold transition-all duration-300 hover:scale-105"
              >
                Download Catalog
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4 items-end">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Products
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                <option value="name">Name</option>
                <option value="category">Category</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <div className="text-sm text-gray-600">
              Showing {sortedProducts.length} of {products.length} products
            </div>
            <div className="flex gap-2">
              <a 
                href="/images/AutoMats_Brochure.pdf"
                download="AutoMats_Product_Catalog.pdf"
                className="bg-automats-red hover:bg-red-700 text-white px-4 py-2 rounded-md font-body font-semibold transition-colors text-sm"
              >
                Request Catalog
              </a>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sortedProducts.map(product => (
            <Card key={product.id} className="hover-lift hover:shadow-xl transition-all duration-300 border-2 hover:border-automats-red/20">
              <CardHeader>
                <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  {productImages[product.name] ? (
                    <img 
                      src={productImages[product.name]} 
                      alt={`${product.name} - AutoMats floor mat product`}
                      className="w-full h-full object-cover rounded-lg bg-white"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="text-center space-y-2" style={{display: productImages[product.name] ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                    <div className="bg-automats-red w-12 h-12 rounded-full flex items-center justify-center mx-auto">
                      <Package className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-sm text-gray-500">Product Image</div>
                  </div>
                </div>
                
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Badge variant="secondary" className="mb-2">
                      {product.category}
                    </Badge>
                    <CardTitle className="text-xl text-gray-900 mb-2">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      {product.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Features */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {product.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <ArrowRight className="h-4 w-4 text-automats-red mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Colors */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Available Colors</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {color}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t pt-4">
                  <div className="flex gap-2">
                    <a
                      href={`mailto:sales@automats.us?subject=Quote%20Request%20-%20${encodeURIComponent(product.name)}&body=Hello%2C%20I%20would%20like%20to%20request%20a%20quote%20for%20the%20${encodeURIComponent(product.name)}.%20Please%20provide%20pricing%20and%20availability%20information.`}
                      className="flex-1 bg-automats-red hover:bg-red-700 text-white px-4 py-2 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105 text-center text-sm"
                    >
                      Request Quote
                    </a>
                    <Link 
                      to={`/products/${product.id}`}
                      className="border-2 border-automats-red text-automats-red hover:bg-automats-red hover:text-white px-4 py-2 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105 text-center text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Enhanced Bottom CTA */}
        <div className="mt-16 bg-white rounded-3xl p-12 shadow-2xl border border-gray-100">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-automats-eco-green/10 text-automats-eco-green border-automats-eco-green/20 px-4 py-2">
                Custom Solutions
              </Badge>
              <h3 className="text-4xl font-heading font-bold text-automats-black">
                Need Something Custom?
              </h3>
              <p className="text-xl font-body text-automats-steel max-w-3xl mx-auto leading-relaxed">
                Can't find exactly what you're looking for? We specialize in custom solutions 
                tailored to your specific vehicle makes and models, including custom logo placement 
                to promote your brand.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-automats-off-white p-6 rounded-xl text-center">
                <div className="bg-automats-red w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-heading font-semibold text-automats-black mb-2">Custom Templates & Branding</h4>
                <p className="text-sm font-body text-automats-steel">Made for specific vehicle makes and models with your logo</p>
              </div>
              <div className="bg-automats-off-white p-6 rounded-xl text-center">
                <div className="bg-automats-eco-green w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Recycle className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-heading font-semibold text-automats-black mb-2">100% Recycled</h4>
                <p className="text-sm font-body text-automats-steel">Same eco-friendly materials in custom floor mats</p>
              </div>
              <div className="bg-automats-off-white p-6 rounded-xl text-center">
                <div className="bg-automats-steel w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <h4 className="font-heading font-semibold text-automats-black mb-2">Expert Support</h4>
                <p className="text-sm font-body text-automats-steel">Direct access to our manufacturing specialists</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a 
                href="mailto:sales@automats.us?subject=Custom Quote Request&body=Hello, I would like to request a custom quote for floor mats. Please contact me to discuss my specific requirements."
                className="bg-automats-red hover:bg-red-700 text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Request Custom Quote
              </a>
              <a 
                href="mailto:sales@automats.us?subject=Specialist Consultation&body=Hello, I would like to speak with a specialist about custom floor mat solutions."
                className="border-2 border-automats-steel text-automats-steel hover:bg-automats-steel hover:text-white px-10 py-4 rounded-xl font-body font-bold text-lg transition-all duration-300 hover:scale-105"
              >
                Talk to Specialist
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;